import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { fetchTenantFeatureFlags, updateTenantFeatureFlag } from '@/lib/api';
import { useAuth } from '@/state/auth-context';
import type {
  TenantFeatureFlagsResponse,
  UpdateTenantFeatureFlagBody,
  UpdateTenantFeatureFlagResponse,
} from '@/types/tenant-feature-flag';

export const TENANT_FEATURE_FLAGS_QUERY_KEY = ['tenantFeatureFlags'] as const;

export function useTenantFeatureFlags(): UseQueryResult<TenantFeatureFlagsResponse> {
  const { getIdToken } = useAuth();

  return useQuery({
    queryKey: TENANT_FEATURE_FLAGS_QUERY_KEY,
    queryFn: async () => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return fetchTenantFeatureFlags(token);
    },
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useUpdateTenantFeatureFlag(): UseMutationResult<
  UpdateTenantFeatureFlagResponse,
  Error,
  { key: string } & UpdateTenantFeatureFlagBody
> {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, ...body }) => {
      const token = getIdToken();
      if (!token) throw new Error('No ID token available. Please log in again.');
      return updateTenantFeatureFlag(key, body, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TENANT_FEATURE_FLAGS_QUERY_KEY });
    },
  });
}
